import { useGetRestaurantRolesApi } from 'apis/restaurants/restaurant-users/use-get-restaurant-roles-api';
import { BaseFilterButton } from 'components/base-table/filters';
import {
  Button,
  ChakraSelect,
  formatOptionsArray,
  FormControl,
  FormLabel,
  HStack,
  Stack,
  useBoolean,
  useDisclosure,
} from 'design-system';
import { usePageParams } from 'hooks/use-page-params';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

type TOption = {
  value: string;
  label: string;
};

type TInputs = {
  role: TOption | undefined;
};

const defaultValues: TInputs = {
  role: undefined,
};

export const RestaurantRoleIdFilterButton = () => {
  const [isActive, setIsActive] = useBoolean(false);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { getPageParams, setPageParams } = usePageParams();
  const params = getPageParams();
  const { isLoading, data: rolesData = [] } = useGetRestaurantRolesApi('app');

  // Update active filter button
  useEffect(() => {
    if (params.roleId) setIsActive.on();
    else setIsActive.off();
  }, [params.roleId]);

  const { control, handleSubmit, formState, reset, setValue } = useForm<TInputs>({
    defaultValues: { ...defaultValues },
  });

  // Update values
  const updateValues = () => {
    const selected = formatOptionsArray(rolesData, 'id', 'name')?.find((item) => {
      return item?.value === Number(params?.roleId);
    });

    setValue('role', selected ?? undefined);
  };

  useEffect(() => {
    updateValues();
  }, [params.roleId, rolesData]);

  const onSubmit = (data: TInputs) => {
    params.roleId = data.role?.value as string;

    setPageParams(params);
    onClose();
    reset(data);
  };

  const handleClose = () => {
    onClose();
    updateValues();
  };

  const handleDisableFilterButton = () => {
    const isDisabled = Object.keys(formState.dirtyFields).length ? false : true;
    return isDisabled;
  };

  return (
    <BaseFilterButton
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={handleClose}
      isActive={isActive}
      formComponent={
        <Stack as='form' spacing='lg' p='md' pt='lg' onSubmit={handleSubmit(onSubmit)}>
          <Controller
            control={control}
            name='role'
            render={({ field: { onChange, name, onBlur, value } }) => (
              <FormControl>
                <FormLabel>Role</FormLabel>
                <ChakraSelect
                  id='role'
                  isLoading={isLoading}
                  options={formatOptionsArray(rolesData, 'id', 'name')}
                  onChange={onChange}
                  name={name}
                  onBlur={onBlur}
                  value={value}
                />
              </FormControl>
            )}
          />

          <HStack justify='flex-end'>
            <Button variant='outline' colorScheme='red' onClick={handleClose}>
              Cancel
            </Button>
            <Button variant='solid' colorScheme='red' type='submit' disabled={handleDisableFilterButton()}>
              Filter
            </Button>
          </HStack>
        </Stack>
      }
    />
  );
};
